import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Sparkles, Loader2 } from 'lucide-react';
import { useArtifactStore } from '../../stores/artifactStore';
import { cn } from '../../lib/utils';

export type AskAgentEntityType = 'domain' | 'usecase' | 'cadrage';

interface AskAgentButtonProps {
  entityType: AskAgentEntityType;
  entityId: string;
  entityName?: string | null;
  className?: string;
}

const buildInitialMessage = (
  entityType: AskAgentEntityType,
  entityId: string,
  entityName?: string | null
): string => {
  const subject =
    entityType === 'domain'
      ? 'domain'
      : entityType === 'usecase'
        ? 'usecase'
        : 'solution';
  const namePart = entityName ? ` "${entityName}"` : '';
  return `J'ai une question par rapport au ${subject}${namePart} (id: ${entityId}).`;
};

export const AskAgentButton: React.FC<AskAgentButtonProps> = ({
  entityType,
  entityId,
  entityName,
  className,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createSession, setPendingMessage } = useArtifactStore();
  const [submitting, setSubmitting] = useState(false);

  const handleClick = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const session = await createSession(null);
      const content = buildInitialMessage(entityType, entityId, entityName);
      setPendingMessage({ sessionId: session.id, content });
      navigate(`/genie-artifact/${session.id}`);
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={submitting}
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold',
        'bg-violet-600 text-white shadow-sm',
        'hover:bg-violet-700 transition-colors',
        'disabled:opacity-70 disabled:cursor-wait',
        className
      )}
      aria-label={t('global.askAgent', 'Ask the Agent')}
      title={t('global.askAgent', 'Ask the Agent')}
    >
      {submitting ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Sparkles className="h-3.5 w-3.5" />
      )}
      <span>{t('global.askAgent', 'Ask the Agent')}</span>
    </button>
  );
};

export default AskAgentButton;
